import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Check, Loader2, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { startGoogleSignIn } from "@/lib/googleAuth";

export default function SubscribePage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sidFromUrl = searchParams.get("subscription_id");  // PayPal subscription
  const stripeSessionId = searchParams.get("session_id");   // Stripe checkout return

  const [config, setConfig] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [activated, setActivated] = useState(false);
  const [waitedLong, setWaitedLong] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const claimedRef = useRef(false);

  // ---------- Redirects ----------
  useEffect(() => {
    if (loading) return;
    // Already premium → hard reload into /browse so AuthContext + content all refresh
    if (user?.is_subscribed) {
      window.location.replace("/browse");
      return;
    }
    // Not signed in AND no pending PayPal payment → kick to Google.
    // If there IS a pending payment in the URL, we render the "Sign in to claim"
    // screen instead — never wipe the subscription_id by redirecting away.
    if (!user && !sidFromUrl) {
      startGoogleSignIn();
    }
  }, [user, loading, sidFromUrl]);

  // ---------- Load PayPal config + plan ----------
  // Fire these immediately (in parallel) — they're PUBLIC endpoints, so we
  // don't need to wait for /auth/me. This saves ~400-800ms on mobile cellular.
  // Hydrate from localStorage first so the PayPal SDK can start loading on
  // first paint instead of waiting for the backend round-trip.
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("ybtv_paypal_cache") || "null");
      if (cached?.config) setConfig(cached.config);
      if (cached?.planId) setPlanId(cached.planId);
    } catch (_) {}

    Promise.allSettled([
      api.get("/payment/config"),
      api.get("/payment/plan"),
    ]).then(([cfgRes, planRes]) => {
      const cfg = cfgRes.status === "fulfilled" ? cfgRes.value.data : null;
      const pid = planRes.status === "fulfilled" ? planRes.value.data?.plan_id : null;
      if (cfg) setConfig(cfg);
      if (pid) setPlanId(pid);
      if (cfg?.paypal_client_id && pid) {
        try {
          localStorage.setItem("ybtv_paypal_cache",
            JSON.stringify({ config: cfg, planId: pid, cachedAt: Date.now() }));
        } catch (_) {}
      }
    });
  }, []);

  // ---------- Bulletproof activation ----------
  const finishWithSuccess = () => {
    setActivated(true);
    // Hard reload — and add ?just_paid=1 so /browse retries /auth/me until
    // is_subscribed=true settles (fixes the "needs multiple refreshes" bug).
    window.location.replace("/browse?just_paid=1");
  };

  const activate = async (subscription_id) => {
    if (claimedRef.current) return;
    claimedRef.current = true;
    setProcessing(true);

    // Show "this is taking longer than usual" after 5s with a manual retry button
    const longTimer = setTimeout(() => setWaitedLong(true), 5000);

    // Attempt 1: authed activation (succeeds for ~95% of users immediately).
    try {
      await api.post("/payment/paypal/subscription/activate", { subscription_id });
      clearTimeout(longTimer);
      finishWithSuccess();
      return;
    } catch (_) {
      // Cookie may have been stripped — fall through to the public claim endpoint.
    }

    // Attempt 2: PUBLIC claim endpoint (no auth) — activates via PayPal webhook.
    try {
      const r = await api.post("/payment/claim", { subscription_id });
      if (r.data?.claimed) {
        clearTimeout(longTimer);
        finishWithSuccess();
        return;
      }
    } catch (_) {}

    // Attempt 3: fast-then-slow polling for up to 30s total (8 attempts).
    // PayPal webhooks usually arrive in <3s; this catches the slow ones.
    const delays = [1500, 2000, 3000, 3000, 4000, 5000, 5000, 6000]; // 29.5s total
    for (const delay of delays) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, delay));
      try {
        // eslint-disable-next-line no-await-in-loop
        const r2 = await api.post("/payment/claim", { subscription_id });
        if (r2.data?.claimed) {
          clearTimeout(longTimer);
          finishWithSuccess();
          return;
        }
      } catch (_) {}
      try {
        // eslint-disable-next-line no-await-in-loop
        const r3 = await api.get("/auth/me");
        if (r3.data?.is_subscribed) {
          clearTimeout(longTimer);
          finishWithSuccess();
          return;
        }
      } catch (_) {}
    }

    // Last resort — show the manual recovery UI
    clearTimeout(longTimer);
    setProcessing(false);
    setWaitedLong(true);
    claimedRef.current = false;
    toast.error(
      "Payment received but activation is taking longer than usual. Tap 'Unlock now' below."
    );
  };

  const manualClaim = async () => {
    if (!sidFromUrl) {
      window.location.replace("/browse?just_paid=1");
      return;
    }
    setProcessing(true);
    try {
      const r = await api.post("/payment/claim", { subscription_id: sidFromUrl });
      if (r.data?.claimed) {
        finishWithSuccess();
        return;
      }
    } catch (_) {}
    window.location.replace("/browse?just_paid=1");
  };

  // ---------- Stripe Checkout ----------
  const startStripe = async () => {
    setStripeLoading(true);
    try {
      const r = await api.post("/payment/stripe/checkout", {
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        // Redirect to Stripe's hosted card form.
        window.location.href = r.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      setStripeLoading(false);
      toast.error(e?.response?.data?.detail || "Could not start card checkout.");
    }
  };

  // ---------- Stripe return polling ----------
  useEffect(() => {
    if (!stripeSessionId || claimedRef.current) return;
    claimedRef.current = true;
    setProcessing(true);
    const longTimer = setTimeout(() => setWaitedLong(true), 5000);

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payment/stripe/status/${stripeSessionId}`);
        if (r.data?.payment_status === "paid") {
          clearTimeout(longTimer);
          finishWithSuccess();
          return;
        }
        if (r.data?.status === "expired") {
          clearTimeout(longTimer);
          setProcessing(false);
          claimedRef.current = false;
          toast.error("Checkout session expired. Please try again.");
          return;
        }
      } catch (_) {}
      if (attempts >= 15) {
        clearTimeout(longTimer);
        setProcessing(false);
        setWaitedLong(true);
        toast.error("Payment status check is slow. Tap 'Unlock now' to retry.");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [stripeSessionId]);

  // ---------- Auto-claim from PayPal full-page redirect (mobile) ----------
  // Two cases:
  //   1. User is signed in → call activate() which tries authed activate first,
  //      then falls back to public /payment/claim + polling.
  //   2. User is signed OUT (cookie stripped by Safari ITP) → call public
  //      /payment/claim directly so the DB user record is activated; then
  //      they only need to sign in once to see the unlocked library.
  useEffect(() => {
    if (!sidFromUrl || claimedRef.current) return;
    if (loading) return;
    if (user && !user.is_subscribed) {
      activate(sidFromUrl);
      return;
    }
    if (!user) {
      // Best-effort public claim — surfaces a success toast on the sign-in screen.
      claimedRef.current = true;
      api.post("/payment/claim", { subscription_id: sidFromUrl })
        .then((r) => {
          if (r.data?.claimed) {
            toast.success("Payment confirmed! Sign in to unlock your library.");
          }
        })
        .catch(() => { claimedRef.current = false; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, sidFromUrl]);

  // ---------- Render branches ----------

  // Signed out + arrived back from PayPal with subscription_id → sign-in-to-claim screen.
  if (!user && sidFromUrl) {
    return (
      <div className="min-h-screen bg-[#0A0A0E] text-white grid place-items-center px-6 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#E50914]/15 blur-[140px]" />
        <div className="relative z-10 text-center max-w-md">
          <div data-testid="claim-icon" className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/40 mb-5">
            <Check className="w-8 h-8 text-[#22c55e]" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Payment received!</h1>
          <p className="mt-3 text-gray-300 leading-relaxed">
            Sign in with the <strong className="text-white">same Google account</strong> you used to start your subscription and your premium access will unlock instantly.
          </p>
          <Button
            data-testid="signin-to-claim"
            onClick={startGoogleSignIn}
            className="mt-6 rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-7 py-6 text-base font-semibold w-full sm:w-auto"
          >
            Sign in with Google to unlock
          </Button>
          <p className="mt-4 text-[11px] text-gray-500">
            Subscription ID: <span className="font-mono">{sidFromUrl}</span>
          </p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Activating / success splash (covers both onApprove and ?subscription_id flows)
  if (activated || processing) {
    return (
      <div className="min-h-screen bg-[#0A0A0E] text-white grid place-items-center px-6 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#E50914]/15 blur-[140px]" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#FFD700]/8 blur-[140px]" />
        <div className="relative z-10 text-center max-w-md w-full">
          {activated ? (
            <>
              <div data-testid="activated-icon" className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/40 mb-5">
                <Check className="w-8 h-8 text-[#22c55e]" />
              </div>
              <h1 data-testid="activated-heading" className="font-display text-3xl md:text-4xl font-bold tracking-tight">You're in!</h1>
              <p className="mt-3 text-gray-300">Unlocking your library…</p>
            </>
          ) : (
            <>
              <Loader2 data-testid="activating-spinner" className="w-12 h-12 mx-auto animate-spin text-[#E50914]" />
              <h1 className="mt-6 font-display text-2xl md:text-3xl font-bold tracking-tight">Unlocking your membership</h1>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">
                {waitedLong
                  ? "Taking a little longer than usual. Tap below to jump straight in — we've already received your payment."
                  : "This usually takes a few seconds. Don't close this page."}
              </p>
              {waitedLong && (
                <Button
                  data-testid="manual-unlock-btn"
                  onClick={manualClaim}
                  className="mt-6 rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-7 py-6 text-base font-semibold w-full sm:w-auto"
                >
                  Unlock now →
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  const live = config?.configured && config?.paypal_client_id && planId;

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#E50914]/15 blur-[140px]" />
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#FFD700]/8 blur-[140px]" />

      <header className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 h-20 flex items-center justify-between">
        <button
          data-testid="back-home"
          onClick={() => navigate("/")}
          className="font-display font-black text-2xl md:text-3xl tracking-tighter text-[#E50914]"
        >
          YBTV
        </button>
        <button
          data-testid="signout-link"
          onClick={async () => { await logout(); navigate("/"); }}
          className="text-sm text-gray-400 hover:text-white"
        >
          Not {user.name?.split(" ")[0]}? Sign out
        </button>
      </header>

      <main className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 pt-8 pb-20 grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-4">
            Step 2 of 2 · Payment
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
            Almost there,<br/>
            <span className="text-[#E50914]">{user.name?.split(" ")[0]}.</span>
          </h1>
          <p className="mt-5 text-gray-300 max-w-md leading-relaxed">
            One safe PayPal checkout and the entire YBTV library unlocks.
          </p>

          <ul className="mt-7 space-y-3 text-gray-200">
            {[
              "Full library — movies, shows, trailers & interviews",
              "HD streaming on any device",
              "Auto-renews monthly. Cancel anytime.",
              "Secure payment through PayPal",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2.5">
                <Check className="w-5 h-5 mt-0.5 text-[#E50914] shrink-0" /> <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex items-center gap-2 text-xs text-gray-500">
            <ShieldCheck className="w-4 h-4 text-[#FFD700]" /> Payments processed securely by PayPal · YBTV never stores your card details
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#141419]/80 backdrop-blur p-7 md:p-9 shadow-2xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-3">YBTV Premium</div>
          <div className="flex items-baseline gap-1">
            <span className="font-display text-5xl md:text-6xl font-black text-white">$3.99</span>
            <span className="text-gray-400">/month</span>
          </div>
          <p className="mt-2 text-sm text-gray-400">Recurring monthly billing. Cancel any time from your account.</p>

          <div className="mt-7 border-t border-white/10 pt-6 space-y-4">
            {/* PayPal checkout */}
            {live ? (
              <div data-testid="paypal-buttons-wrap">
                <PayPalScriptProvider
                  options={{
                    clientId: config.paypal_client_id,
                    vault: true,
                    intent: "subscription",
                    currency: "USD",
                  }}
                >
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "pill", color: "gold", label: "subscribe" }}
                    createSubscription={(data, actions) =>
                      actions.subscription.create({
                        plan_id: planId,
                        custom_id: user.user_id,
                        application_context: {
                          brand_name: "YBTV",
                          user_action: "SUBSCRIBE_NOW",
                          return_url: `${window.location.origin}/subscribe`,
                          cancel_url: `${window.location.origin}/subscribe`,
                        },
                      })
                    }
                    onApprove={async (data) => { await activate(data.subscriptionID); }}
                    onError={() => toast.error("PayPal checkout error.")}
                    onCancel={() => toast.message("Checkout cancelled")}
                  />
                </PayPalScriptProvider>
              </div>
            ) : (
              <div className="text-center py-6 space-y-3">
                <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#E50914]" />
                <p className="text-sm text-gray-400">Loading PayPal…</p>
              </div>
            )}
          </div>

          <div className="mt-5 flex items-center justify-center gap-2 text-[11px] text-gray-500">
            <Lock className="w-3 h-3" /> Encrypted checkout · You can cancel any time
          </div>

          <button
            data-testid="recover-btn"
            onClick={async () => {
              setProcessing(true);
              try {
                const r = await api.post("/payment/recover");
                if (r.data.recovered) {
                  finishWithSuccess();
                } else {
                  setProcessing(false);
                  toast.message(r.data.message || "No paid subscription found yet — wait 30 seconds and try again.");
                }
              } catch (e) {
                setProcessing(false);
                toast.error("Could not recover. Please email support@youngblack.tv with your PayPal receipt.");
              }
            }}
            className="mt-4 w-full text-xs text-gray-500 hover:text-white underline decoration-dotted underline-offset-4"
          >
            Already paid but not unlocked? Tap to recover →
          </button>
        </div>
      </main>
    </div>
  );
}
